import { useEffect, useState } from 'react';

import { Col } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Dialog } from '@opendoor/atoms/dialog';
import { Text } from '@opendoor/atoms/text';
import { useAuth } from '@opendoor/auth-fe/context';
import { IconX } from '@tabler/icons-react';
import type { ILpComponentGetCashOfferModalFields } from 'declarations/contentful';

const GetCashOfferModal = (entry: ILpComponentGetCashOfferModalFields) => {
  const { authentication } = useAuth();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (authentication.state === 'not_authenticated') {
      const hasShownDialog = localStorage.getItem('getCashOfferModalShown');

      if (!hasShownDialog) {
        timer = setTimeout(() => {
          setIsOpen(true);
          localStorage.setItem('getCashOfferModalShown', 'true');
        }, 400);
      }
    }
    return () => clearTimeout(timer);
  }, [authentication.state]);

  const analyticsName = `cosmos-landing-page-visitor-modal-${entry?.analyticsId}`;

  return (
    <Dialog.Root role="dialog" open={isOpen} onOpenChange={setIsOpen}>
      <Dialog hideCloseButton className="sm:max-w-130">
        {/* Desktop-only close (X). The Bricks dialog had no drag handle and was not dismissable
            on small screens, where the explicit Close button below is the only way to close. */}
        <Dialog.Close
          render={(props) => (
            <Button
              {...props}
              variant="secondary"
              size="sm"
              analyticsName={analyticsName}
              aria-label="Close"
              className="absolute top-6 right-6 h-11 w-11 rounded-full border-0 p-0 max-sm:hidden"
            >
              <IconX size={24} aria-hidden="true" />
            </Button>
          )}
        />
        {/* The Bricks dialog rendered its title at body size, so the accessible title (which
            Atoms styles as a header) stays visually hidden and the visible title is body text.
            aria-hidden keeps the title from being announced twice. */}
        <Dialog.Title className="sr-only">{entry.title}</Dialog.Title>
        <Text aria-hidden="true" className="pb-4 text-body-md text-od-content-primary sm:pr-8">
          {entry.title}
        </Text>
        <Dialog.Description className="text-body-md text-od-content-secondary">
          {entry.description}
        </Dialog.Description>
        <Col className="gap-2 pt-8 md:pt-12">
          <Button
            className="w-full"
            variant="primary"
            size="lg"
            analyticsName={`${analyticsName}-cash-offer-cta`}
            aria-label="Get a cash offer"
            onClick={() => {
              document.location.href = 'https://www.opendoor.com/address-entry';
            }}
          >
            Get a cash offer
          </Button>
          {/* Mobile-only close action — the bottom sheet has no dismiss chrome on small screens. */}
          <Button
            className="w-full sm:hidden"
            variant="secondary"
            size="lg"
            analyticsName={analyticsName}
            aria-label="Close"
            onClick={() => setIsOpen(false)}
          >
            Close
          </Button>
        </Col>
      </Dialog>
    </Dialog.Root>
  );
};

export default GetCashOfferModal;
